import { Star, X, Share2, Copy, TriangleAlert, Volume2, VolumeX } from 'lucide-react';
import { categories } from '../data/categories';
import { CATEGORY_ICONS } from '../data/icons';
import { detectPlatform } from '../utils/video';
import { shareTechnique } from '../utils/share';
import { isMobile } from './PipPlayer';

// The subtitle is a category label; map it back to that category's icon so the
// header shows the matching glyph beside it.
const SUBTITLE_ICON = new Map(categories.map((c) => [c.label, CATEGORY_ICONS[c.id]]));

const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

interface PlayerHeaderProps {
  /** Technique name shown as the title */
  title?: string;
  /** Optional category / context line under the title */
  subtitle?: string;
  /** Whether the technique is already favorited */
  starred?: boolean;
  /** Provide to render the star button (favorite toggle). Omit to hide it. */
  onStar?: () => void;
  /** Provide to render the close button. Omit to hide it (e.g. the Discover feed). */
  onClose?: () => void;
  /** Drag handle — used by the floating PiP widget to grab the toolbar. */
  onPointerDown?: (e: React.PointerEvent) => void;
  /** Extra inline styles (e.g. the Discover feed's z-index). */
  style?: React.CSSProperties;
  /** The original post this clip came from — renders the credit link out to it. */
  sourceUrl?: string;
  /** Poster handle, shown in the credit line when known. */
  author?: string;
  /** Sport rules note, shown under the credit. */
  legality?: string;
  /** The athlete in the clip, when that's someone other than the poster:
   *  its own line under the credit, so it never reads as a second poster. */
  attribution?: string;
  /** Given, the handle opens the creator's page in the app instead of linking
   *  out; the Instagram link is then on that page. The player closes behind
   *  it — the creator's page is the view being asked for, and on mobile the
   *  player covers the whole screen. */
  onAuthor?: (handle: string) => void;
  /** Enables the copy-link button — the id is what a share link points at. */
  techniqueId?: string;
  /** Whether the clip is currently audible — only read when onToggleSound is
   *  given. */
  soundOn?: boolean;
  /** Provide to render the speaker button up here. The feed uses it for embeds,
   *  whose own controls sit at the bottom of the frame where a button of ours
   *  would cover them. Omit to hide it. */
  onToggleSound?: () => void;
}

// Shared header for the Discover feed and the PiP player so the two never drift.
// Per-instance differences (drag handle, z-index, whether the star shows) are
// explicit props; layout, icons, and tooltips live here only.
export default function PlayerHeader({ title, subtitle, starred, onStar, onClose, onPointerDown, style, sourceUrl, author, attribution, legality, onAuthor, techniqueId, soundOn, onToggleSound }: PlayerHeaderProps) {
  const SubIcon = subtitle ? SUBTITLE_ICON.get(subtitle) : undefined;
  // Credit line: every clip belongs to whoever posted it, so the player links
  // back to the original post. Nothing to link to means nothing to render.
  const platform = sourceUrl ? detectPlatform(sourceUrl) : null;
  const platformLabel = platform ? PLATFORM_LABEL[platform] : null;
  // Handle alone when we know it; the platform name only stands in when we
  // don't, so the way back to the original post never disappears entirely.
  const creditLabel = author ? `@${author}` : platformLabel;
  const mobile = isMobile();
  return (
    <div className="pip-toolbar" onPointerDown={onPointerDown} style={style}>
      <div className="pip-toolbar-info">
        <span className="pip-title">{title}</span>
        {(subtitle || creditLabel) && (
          <span className="pip-toolbar-meta">
            {subtitle && (
              <span className="pip-toolbar-sub">
                {SubIcon && <SubIcon size={13} />}
                {subtitle}
              </span>
            )}
            {subtitle && creditLabel && <span className="pip-toolbar-dot">&middot;</span>}
            {author && onAuthor ? (
              <button
                type="button"
                className="pip-toolbar-credit"
                title={`See more from @${author}`}
                // The toolbar doubles as the PiP's drag handle — keep it off
                // this button.
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onAuthor(author); onClose?.(); }}
              >
                @{author}
              </button>
            ) : creditLabel && (
              <a
                className="pip-toolbar-credit"
                href={sourceUrl}
                target="_blank"
                rel="noopener"
                title={`Open the original post on ${platformLabel ?? "Instagram"}`}
                // The toolbar doubles as the PiP's drag handle and sits over the
                // feed's swipe area — keep both off this link.
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {creditLabel}
              </a>
            )}
          </span>
        )}
        {attribution && <span className="pip-toolbar-ft">ft. {attribution}</span>}
        {legality && <span className="pip-toolbar-rule" title={legality}><TriangleAlert size={11} /> {legality}</span>}
      </div>
      {onToggleSound && (
        <button
          className="pip-share-btn"
          onClick={onToggleSound}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={soundOn ? 'Mute' : 'Unmute'}
          title={soundOn ? 'Mute' : 'Unmute'}
        >
          {soundOn ? <Volume2 size={26} /> : <VolumeX size={26} />}
        </button>
      )}
      {techniqueId && (
        <button
          className="pip-share-btn"
          onClick={() => shareTechnique(techniqueId, title ?? 'Technique', mobile)}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={mobile ? 'Share technique' : 'Copy link to technique'}
          title={mobile ? 'Share this technique' : 'Copy link to this technique'}
        >
          {mobile ? <Share2 size={26} /> : <Copy size={26} />}
        </button>
      )}
      {onStar && (
        <button
          className={`feed-star-btn${starred ? ' feed-star-btn--active' : ''}`}
          onClick={onStar}
          title={starred ? 'In favorites' : 'Add to favorites'}
        >
          <Star size={32} fill={starred ? 'currentColor' : 'none'} />
        </button>
      )}
      {onClose && (
        <button className="pip-close-btn" onClick={onClose}>
          <X size={28} />
        </button>
      )}
    </div>
  );
}
